import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Area, AreaChart } from 'recharts';
import dashboardService from '../services/dashboardService';
import { salesTeamService } from '../services/salesTeamService';

const API_BASE_URL = 'https://apisaleeasy.psisat.com';

// Color palette
const COLORS = {
  neonBlue: '#00d4ff',
  neonCyan: '#00f5d4',
  neonPurple: '#b388ff',
  neonPink: '#ff6ec7',
  neonGreen: '#39ff14',
  neonYellow: '#ffe66d',
  neonOrange: '#ff9f43',
  darkBg: '#0a0e27',
  cardBg: 'rgba(13, 25, 60, 0.85)',
  cardBorder: 'rgba(0, 212, 255, 0.2)',
  textPrimary: '#e0e6ff',
  textSecondary: '#7b8cde',
  gridLine: 'rgba(0, 212, 255, 0.1)',
};

const TEAM_COLORS = ['#00d4ff', '#00f5d4', '#b388ff', '#ff6ec7', '#ff9f43', '#ffe66d'];

const formatNumber = (num) => {
  if (!num && num !== 0) return '0';
  return Number(num).toLocaleString('th-TH');
};

const formatCurrency = (num) => {
  if (!num && num !== 0) return '0.00';
  if (num >= 1000000) return (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatFullCurrency = (num) => {
  if (!num && num !== 0) return '0.00';
  return Number(num).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// CSS for animated border
const glowCardStyles = document.createElement('style');
glowCardStyles.textContent = `
  @keyframes borderGlow {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  @keyframes lightTrace {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
if (!document.querySelector('#glow-card-styles')) {
  glowCardStyles.id = 'glow-card-styles';
  document.head.appendChild(glowCardStyles);
}

// Glowing card wrapper with animated border
const GlowCard = ({ children, title, icon, className = '', glowColor = COLORS.neonBlue }) => (
  <div className={`relative rounded-2xl flex flex-col ${className}`}>
    {/* Animated border - light trace effect on border only */}
    <div
      className="absolute inset-0 rounded-2xl overflow-hidden"
      style={{ mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)', maskComposite: 'exclude', WebkitMaskComposite: 'xor', padding: '1.5px' }}
    >
      <div
        className="absolute"
        style={{
          width: '200%',
          height: '200%',
          top: '-50%',
          left: '-50%',
          background: `conic-gradient(from 0deg, transparent 0%, ${glowColor} 10%, transparent 20%, transparent 50%, ${glowColor}80 60%, transparent 70%)`,
          animation: 'lightTrace 4s linear infinite',
        }}
      />
    </div>
    {/* Card inner */}
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col flex-1 z-10"
      style={{
        background: COLORS.cardBg,
        boxShadow: `0 0 15px ${glowColor}10, inset 0 1px 0 rgba(255,255,255,0.05)`,
      }}
    >
      {title && (
        <div className="flex items-center gap-2 px-4 pt-3 pb-1 flex-shrink-0">
          {icon && <span style={{ color: glowColor }}>{icon}</span>}
          <h3 className="text-xs font-bold tracking-wider uppercase" style={{ color: glowColor, letterSpacing: '0.1em' }}>
            {title}
          </h3>
        </div>
      )}
      <div className="px-4 pb-3 flex-1 overflow-hidden">
        {children}
      </div>
    </div>
  </div>
);

// Animated number display
const AnimatedNumber = ({ value, prefix = '', suffix = '', size = 'text-3xl', color = COLORS.neonCyan }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const target = Number(value) || 0;
    const duration = 1000;
    const steps = 30;
    const increment = target / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(target, increment * step);
      setDisplayValue(current);
      if (step >= steps) {
        setDisplayValue(target);
        clearInterval(timer);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [value]);

  return (
    <span className={`${size} font-bold font-mono`} style={{ color, textShadow: `0 0 20px ${color}40` }}>
      {prefix}{formatFullCurrency(displayValue)}{suffix}
    </span>
  );
};

// Circular gauge component
const CircularGauge = ({ percentage, label, color, size = 120 }) => {
  const radius = (size - 16) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(percentage, 100);
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke="rgba(0,212,255,0.1)" strokeWidth="8"
        />
        {/* Progress circle */}
        <circle
          cx={size / 2} cy={size / 2} r={radius}
          fill="none" stroke={color} strokeWidth="8"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 1.5s ease-in-out', filter: `drop-shadow(0 0 6px ${color})` }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-xl font-bold font-mono" style={{ color }}>{Math.round(progress)}%</span>
      </div>
      <span className="text-xs mt-1 font-medium" style={{ color: COLORS.textSecondary }}>{label}</span>
    </div>
  );
};

// Sparkline mini chart
const SparkLine = ({ data, color, height = 40 }) => (
  <ResponsiveContainer width="100%" height={height}>
    <AreaChart data={data}>
      <defs>
        <linearGradient id={`spark-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
      </defs>
      <Area type="monotone" dataKey="value" stroke={color} fill={`url(#spark-${color.replace('#', '')})`} strokeWidth={2} dot={false} />
    </AreaChart>
  </ResponsiveContainer>
);

const MonitorDashboardComponent = () => {
  // State
  const [currentTime, setCurrentTime] = useState(new Date());
  const [hourlySalesData, setHourlySalesData] = useState([]);
  const [dailySalesData, setDailySalesData] = useState([]);
  const [teamSalesData, setTeamSalesData] = useState([]);
  const [salesmanStats, setSalesmanStats] = useState(null);
  const [topSalesmen, setTopSalesmen] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [latestInvoices, setLatestInvoices] = useState([]);
  const [latestProducts, setLatestProducts] = useState([]);
  const [approvedQuotations, setApprovedQuotations] = useState([]);
  const [teamSalesTotal, setTeamSalesTotal] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [refreshCountdown, setRefreshCountdown] = useState(60);

  const toLocalDateStr = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const getTodayRange = useCallback(() => {
    const dateStr = toLocalDateStr(new Date());
    return { startDate: dateStr, endDate: dateStr };
  }, []);

  const getWeekRange = useCallback(() => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 7);
    return {
      startDate: toLocalDateStr(start),
      endDate: toLocalDateStr(end)
    };
  }, []);

  // Clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Refresh countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setRefreshCountdown(prev => {
        if (prev <= 1) return 60;
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch all data
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    const today = getTodayRange();
    const week = getWeekRange();

    try {
      const [hourlyResult, dailyResult, teamResult, salesmanResult, combinedResult, teamsWithMembersResult, invoiceReportResult, topProductsResult] = await Promise.allSettled([
        dashboardService.getHourlySales(today.startDate, today.endDate),
        dashboardService.getDailySales(week.startDate, week.endDate),
        salesTeamService.getTeamSalesSummary(today.startDate, today.endDate),
        fetch(`${API_BASE_URL}/api/sales-analytics/salesman-summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            date_range: { start_date: today.startDate, end_date: today.endDate, group_by: 'month' },
            filters: { company_code: '045' }
          })
        }).then(r => r.json()),
        // Use same API as Dashboard overview (team-sales-summary)
        salesTeamService.getTeamSalesSummary(today.startDate, today.endDate),
        salesTeamService.getTeamsWithMembers(),
        fetch(`${API_BASE_URL}/api/report/invoicereport`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ startDate: today.startDate, endDate: today.endDate, companyCode: '045' })
        }).then(r => r.json()),
        dashboardService.getTopProducts(today.startDate, today.endDate, 10),
      ]);

      // Build salesman code -> name map from teams API
      const salesmanNameMap = {};
      if (teamsWithMembersResult.status === 'fulfilled' && teamsWithMembersResult.value.success) {
        const allTeams = teamsWithMembersResult.value.data || [];
        allTeams.forEach(team => {
          const members = team.members || [];
          members.forEach(m => {
            const code = m.member_saleman_code || '';
            const name = [m.sale_name, m.sale_lastname].filter(Boolean).join(' ');
            if (code && name) salesmanNameMap[code] = name;
          });
        });
      }

      // Hourly sales
      if (hourlyResult.status === 'fulfilled' && hourlyResult.value.success) {
        const transformed = hourlyResult.value.data.map(item => ({
          hour: item.hour_display,
          quotation: item.total_quotation_value,
          invoice: item.total_invoice_value,
          qCount: item.quotation_count,
          iCount: item.invoice_count,
        }));
        setHourlySalesData(transformed);
        if (hourlyResult.value.executive_summary) {
          setSummaryData(hourlyResult.value.executive_summary);
        }
      }

      // Daily sales
      if (dailyResult.status === 'fulfilled' && dailyResult.value.success) {
        const transformed = dailyResult.value.data.map(item => ({
          date: item.date_display,
          day: item.day_of_week,
          quotation: item.total_quotation_value,
          invoice: item.total_invoice_value,
          qCount: item.quotation_count,
          iCount: item.invoice_count,
        }));
        setDailySalesData(transformed);
      }

      // Team sales
      if (teamResult.status === 'fulfilled' && teamResult.value.success && teamResult.value.data) {
        const teams = teamResult.value.data.teams || teamResult.value.data;
        if (Array.isArray(teams)) {
          const teamData = teams.map((team, idx) => ({
            name: team.team_name || team.TeamName || `Team ${idx + 1}`,
            amount: team.total_amount || team.TotalAmount || 0,
            bills: team.total_bills || team.TotalBills || 0,
            members: team.member_count || team.MemberCount || 0,
            color: TEAM_COLORS[idx % TEAM_COLORS.length],
          }));
          setTeamSalesData(teamData);
        }
      }

      // Salesman stats - resolve names from salesmanNameMap
      if (salesmanResult.status === 'fulfilled') {
        const result = salesmanResult.value;
        if (result.status && result.data) {
          setSalesmanStats(result.data);
          const salesmen = result.data.salesmen || result.data.salesman_list || [];
          if (Array.isArray(salesmen)) {
            const sorted = [...salesmen]
              .filter(sm => (sm.total_amount || sm.TotalAmount || 0) > 0)
              .sort((a, b) => (b.total_amount || b.TotalAmount || 0) - (a.total_amount || a.TotalAmount || 0))
              .slice(0, 10)
              .map(sm => ({
                ...sm,
                resolved_name: salesmanNameMap[sm.salesman_code] || sm.salesman_code || 'N/A'
              }));
            setTopSalesmen(sorted);
          }
        }
      }

      // Combined sales total (same as Dashboard overview)
      if (combinedResult.status === 'fulfilled' && combinedResult.value.success && combinedResult.value.data) {
        const teams = combinedResult.value.data.teams || combinedResult.value.data;
        if (Array.isArray(teams)) {
          const totalAmount = teams.reduce((sum, t) => sum + (t.total_amount || 0), 0);
          const totalBills = teams.reduce((sum, t) => sum + (t.total_bills || 0), 0);
          setTeamSalesTotal({ totalAmount, totalBills });
        }
      }

      // Shared customer name map (used by both latest invoices and timeline sections)
      const custNameMapShared = {};

      // Latest invoices (20 customers) - resolve customer names
      if (invoiceReportResult.status === 'fulfilled') {
        const result = invoiceReportResult.value;
        const invoices = result?.data?.invoices || [];
        const sorted = [...invoices]
          .filter(inv => (inv.TotalAmount || inv.Amount || 0) > 0)
          .filter(inv => !/^[1-6]-/.test((inv.Inv_Number || '').trim()))
          .sort((a, b) => new Date(b.Inv_Date) - new Date(a.Inv_Date) || (b.Inv_Number || '').localeCompare(a.Inv_Number || ''))
          .slice(0, 20);

        // Collect unique customer numbers to resolve names
        const uniqueCustNos = [...new Set(sorted.map(inv => (inv.Cust_No || '').trim()).filter(Boolean))];

        // Fetch customer names in batches of 5
        const custNameMap = {};
        for (let i = 0; i < uniqueCustNos.length; i += 5) {
          const batch = uniqueCustNos.slice(i, i + 5);
          const results = await Promise.allSettled(
            batch.map(custNo =>
              fetch(`${API_BASE_URL}/api/customererp/by-custno`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ custNo, companyCode: '045' })
              }).then(r => r.json())
            )
          );
          results.forEach((res, idx) => {
            if (res.status === 'fulfilled' && res.value.status && res.value.data) {
              const resolvedName = (res.value.data.Cust_Name || '').trim();
              custNameMap[batch[idx]] = resolvedName;
              custNameMapShared[batch[idx]] = resolvedName;
            }
          });
        }

        const invoiceData = sorted.map(inv => {
          const custNo = (inv.Cust_No || '').trim();
          return {
            invNumber: (inv.Inv_Number || '').trim(),
            custName: custNameMap[custNo] || (inv.TaxInvoice_Cust_Name || '').trim() || custNo,
            custNo,
            amount: inv.TotalAmount || inv.Amount || 0,
            date: inv.Inv_Date,
          };
        });
        setLatestInvoices(invoiceData);
      }

      // Top 10 products
      if (topProductsResult.status === 'fulfilled' && topProductsResult.value.success) {
        const products = topProductsResult.value.data || [];
        setLatestProducts(products.filter(p => p.product_code && p.product_code.trim() !== 'X0100001').slice(0, 10));
      }

      // Invoice timeline status - fetch shipment data for each invoice
      if (invoiceReportResult.status === 'fulfilled') {
        const result = invoiceReportResult.value;
        const invoices = result?.data?.invoices || [];
        const validInvoices = invoices
          .filter(inv => (inv.TotalAmount || inv.Amount || 0) > 0)
          .filter(inv => !/^[1-6]-/.test((inv.Inv_Number || '').trim()))
          .slice(0, 15);

        // Fetch shipment status for each invoice (batch of 5)
        const invoicesWithStatus = [];
        for (let i = 0; i < validInvoices.length; i += 5) {
          const batch = validInvoices.slice(i, i + 5);
          const results = await Promise.allSettled(
            batch.map(inv => {
              const invNo = (inv.Inv_Number || '').trim();
              return fetch(`${API_BASE_URL}/api/shipments/invoice/045/${invNo}`).then(r => r.json());
            })
          );
          batch.forEach((inv, idx) => {
            const invNo = (inv.Inv_Number || '').trim();
            const custNo = (inv.Cust_No || '').trim();
            const custName = custNameMapShared[custNo] || (inv.TaxInvoice_Cust_Name || '').trim() || custNo;
            let stepLabel = 'รอดำเนินการ';
            let stepColor = COLORS.textSecondary;

            if (results[idx].status === 'fulfilled') {
              const ship = results[idx].value?.data?.[0];
              if (ship) {
                if (ship.shipped_at) { stepLabel = 'จัดส่งแล้ว'; stepColor = COLORS.neonGreen; }
                else if (ship.checked_at) { stepLabel = 'ตรวจเช็คแล้ว'; stepColor = COLORS.neonCyan; }
                else if (ship.packing_at) { stepLabel = 'จัดของเรียบร้อย'; stepColor = COLORS.neonBlue; }
                else if (ship.prepared_at) { stepLabel = 'จัดของเสร็จ'; stepColor = COLORS.neonOrange; }
                else if (ship.printed_at) { stepLabel = 'พิมพ์บิลแล้ว'; stepColor = COLORS.neonYellow; }
                else { stepLabel = 'ทีมอนุมัติแล้ว'; stepColor = COLORS.neonPurple; }
              }
            }
            invoicesWithStatus.push({ code: invNo, name: custName, saleName: salesmanNameMap[(inv.SalesmanCode || '').trim()] || (inv.SalesmanCode || '').trim(), stepLabel, stepColor });
          });
        }
        setApprovedQuotations(invoicesWithStatus);
      }

    } catch (err) {
      console.error('Monitor fetch error:', err);
    } finally {
      setIsLoading(false);
      setLastRefresh(new Date());
      setRefreshCountdown(60);
    }
  }, [getTodayRange, getWeekRange]);

  // Initial load + auto-refresh every 60s
  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 60000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  // Calculate summary stats
  const todayInvoiceTotal = hourlySalesData.reduce((sum, h) => sum + (h.invoice || 0), 0);
  const todayQuotationTotal = hourlySalesData.reduce((sum, h) => sum + (h.quotation || 0), 0);
  const todayInvoiceCount = hourlySalesData.reduce((sum, h) => sum + (h.iCount || 0), 0);
  const todayQuotationCount = hourlySalesData.reduce((sum, h) => sum + (h.qCount || 0), 0);
  const totalTeamAmount = teamSalesData.reduce((sum, t) => sum + (t.amount || 0), 0);

  // Custom tooltip
  const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-lg px-3 py-2 text-xs" style={{ background: 'rgba(10,14,39,0.95)', border: `1px solid ${COLORS.cardBorder}` }}>
        <p className="font-semibold mb-1" style={{ color: COLORS.textPrimary }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }}>
            {p.name}: {formatCurrency(p.value)}
          </p>
        ))}
      </div>
    );
  };

  const thaiDate = currentTime.toLocaleDateString('th-TH', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
  });
  const thaiTime = currentTime.toLocaleTimeString('th-TH', {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });

  return (
    <div className="min-h-screen w-full overflow-hidden" style={{ background: `linear-gradient(135deg, ${COLORS.darkBg} 0%, #0d1942 50%, #0a0e27 100%)` }}>
      {/* Animated background particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute w-96 h-96 rounded-full opacity-5 animate-pulse" style={{ background: `radial-gradient(circle, ${COLORS.neonBlue}, transparent)`, top: '10%', left: '20%' }} />
        <div className="absolute w-64 h-64 rounded-full opacity-5 animate-pulse" style={{ background: `radial-gradient(circle, ${COLORS.neonPurple}, transparent)`, bottom: '20%', right: '15%', animationDelay: '1s' }} />
        <div className="absolute w-48 h-48 rounded-full opacity-5 animate-pulse" style={{ background: `radial-gradient(circle, ${COLORS.neonCyan}, transparent)`, top: '50%', left: '60%', animationDelay: '2s' }} />
      </div>

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between px-4 py-2" style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
        <div className="flex items-center gap-4">
          <img src="/logo-psi2018.png" alt="PSI" className="w-10 h-10 rounded-xl object-contain" style={{ background: 'white', padding: '2px' }} />
          <div>
            <h1 className="text-xl font-bold tracking-wide" style={{ color: COLORS.textPrimary }}>
              PSI Sales Monitor
            </h1>
            <p className="text-xs" style={{ color: COLORS.textSecondary }}>Real-time Sales Dashboard</p>
          </div>
        </div>

        <div className="flex items-center gap-6">
          {/* Fullscreen button */}
          <button
            onClick={() => {
              if (!document.fullscreenElement) {
                document.documentElement.requestFullscreen().catch(() => {});
              } else {
                document.exitFullscreen().catch(() => {});
              }
            }}
            className="p-1.5 rounded-lg transition-colors hover:bg-white/10 cursor-pointer"
            title="เต็มจอ"
            style={{ color: COLORS.textSecondary }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5" />
            </svg>
          </button>

          {/* Refresh indicator */}
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${isLoading ? 'animate-pulse' : ''}`} style={{ background: isLoading ? COLORS.neonYellow : COLORS.neonGreen, boxShadow: `0 0 8px ${isLoading ? COLORS.neonYellow : COLORS.neonGreen}` }} />
            <span className="text-xs font-mono" style={{ color: COLORS.textSecondary }}>
              {isLoading ? 'Loading...' : `${refreshCountdown}s`}
            </span>
          </div>
        </div>
      </header>

      {/* Main Grid - 3x3, columns ratio 1:2:2 */}
      <main className="relative z-10 p-3 pb-7 grid gap-3" style={{ height: 'calc(100vh - 52px)', gridTemplateColumns: '1fr 2fr 2fr', gridTemplateRows: '1fr 1fr 1fr' }}>

        {/* === Col 1: รวม 3 rows เป็น flex column === */}
        <div className="h-full min-h-0 flex flex-col gap-3" style={{ gridRow: 'span 3' }}>
          {/* ยอดขายวันนี้ - เล็ก */}
          <div style={{ flex: '0.7' }} className="min-h-0">
            <GlowCard title="ยอดขายวันนี้" icon="💰" glowColor={COLORS.neonCyan} className="h-full">
              <div className="flex flex-col justify-center items-center h-full gap-1">
                <AnimatedNumber value={teamSalesTotal?.totalAmount || todayInvoiceTotal} suffix=" บาท" color={COLORS.neonCyan} size="text-2xl" />
                <div className="rounded-lg px-4 py-1" style={{ background: 'rgba(0,212,255,0.05)' }}>
                  <p className="text-xs text-center" style={{ color: COLORS.textSecondary }}>จำนวน Invoice</p>
                  <p className="text-3xl font-bold font-mono text-center" style={{ color: COLORS.neonBlue }}>{formatNumber(teamSalesTotal?.totalBills || todayInvoiceCount)}</p>
                </div>
              </div>
            </GlowCard>
          </div>
          {/* พนักงานขายวันนี้ล่าสุด - ใหญ่ */}
          <div style={{ flex: '1.3' }} className="min-h-0">
            <GlowCard title="พนักงานขายวันนี้ล่าสุด" icon="🏆" glowColor={COLORS.neonYellow} className="h-full">
              <div className="overflow-auto h-full">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>พนักงาน</th>
                      <th className="text-right py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>บิล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSalesmen.length > 0 ? topSalesmen.map((sm, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.gridLine}` }}>
                        <td className="py-1 px-1" style={{ color: COLORS.textPrimary }}>{sm.resolved_name || sm.salesman_code}</td>
                        <td className="py-1 px-1 text-right font-mono" style={{ color: COLORS.textSecondary }}>{sm.total_bills || 0}</td>
                      </tr>
                    )) : (
                      <tr><td colSpan={2} className="py-4 text-center" style={{ color: COLORS.textSecondary }}>ไม่มีข้อมูล</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </GlowCard>
          </div>
          {/* สัดส่วนยอดขายตามทีม */}
          <div style={{ flex: '1' }} className="min-h-0">
            <GlowCard title="สัดส่วนยอดขายตามทีม" icon="🎯" glowColor={COLORS.neonPurple} className="h-full">
              {teamSalesData.length > 0 ? (
                <div className="flex flex-col items-center h-full">
                  <div className="flex-1 w-full" style={{ minHeight: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={teamSalesData} cx="50%" cy="50%" innerRadius="35%" outerRadius="60%" dataKey="amount" nameKey="name" stroke="none">
                          {teamSalesData.map((entry, index) => (
                            <Cell key={index} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${formatCurrency(value)} บาท`} contentStyle={{ background: 'rgba(10,14,39,0.95)', border: `1px solid ${COLORS.cardBorder}`, borderRadius: '8px', fontSize: '11px' }} itemStyle={{ color: COLORS.textPrimary }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 w-full flex-shrink-0 mt-1">
                    {teamSalesData.map((team, idx) => {
                      const pct = totalTeamAmount > 0 ? ((team.amount / totalTeamAmount) * 100).toFixed(1) : 0;
                      return (
                        <div key={idx} className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: team.color }} />
                          <span className="text-xs truncate" style={{ color: COLORS.textSecondary }}>{team.name}</span>
                          <span className="text-xs font-mono ml-auto" style={{ color: team.color }}>{pct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full"><p style={{ color: COLORS.textSecondary }}>กำลังโหลด...</p></div>
              )}
            </GlowCard>
          </div>
        </div>

        {/* === Row 1, Col 2: ลูกค้าที่ซื้อสินค้าล่าสุด === */}
        <div className="h-full min-h-0">
          <GlowCard title="ลูกค้าที่ซื้อสินค้าล่าสุด" icon="🛒" glowColor={COLORS.neonBlue} className="h-full">
            {latestInvoices.length > 0 ? (
              <div className="overflow-auto h-full">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>#</th>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>ลูกค้า</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestInvoices.map((inv, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.gridLine}` }}>
                        <td className="py-1 px-1 font-mono" style={{ color: COLORS.textSecondary }}>{idx + 1}</td>
                        <td className="py-1 px-1 truncate" style={{ color: COLORS.textPrimary }} title={inv.custName}>{inv.custName || inv.custNo}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full"><p style={{ color: COLORS.textSecondary }}>กำลังโหลด...</p></div>
            )}
          </GlowCard>
        </div>

        {/* === Row 1-2, Col 3: สถานะคำสั่งซื้อวันนี้ (span 2 rows) === */}
        <div className="h-full min-h-0" style={{ gridRow: 'span 2' }}>
          <GlowCard title="สถานะคำสั่งซื้อวันนี้" icon="✅" glowColor={COLORS.neonGreen} className="h-full">
            {approvedQuotations.length > 0 ? (
              <div className="overflow-auto h-full">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>#</th>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>ลูกค้า</th>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>พนักงาน</th>
                      <th className="text-center py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedQuotations.map((q, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.gridLine}` }}>
                        <td className="py-1 px-1 font-mono" style={{ color: COLORS.textSecondary }}>{idx + 1}</td>
                        <td className="py-1 px-1 truncate max-w-[160px]" style={{ color: COLORS.textPrimary }} title={q.name}>{q.name}</td>
                        <td className="py-1 px-1 truncate max-w-[100px]" style={{ color: COLORS.textSecondary }}>{q.saleName}</td>
                        <td className="py-1 px-1 text-center">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ background: `${q.stepColor}15`, color: q.stepColor, border: `1px solid ${q.stepColor}30` }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: q.stepColor }} />
                            {q.stepLabel}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full"><p style={{ color: COLORS.textSecondary }}>กำลังโหลด...</p></div>
            )}
          </GlowCard>
        </div>

        {/* === Row 2, Col 2: 10 สินค้าขายดีวันนี้ === */}
        <div className="h-full min-h-0">
          <GlowCard title="10 สินค้าขายดีวันนี้" icon="📦" glowColor={COLORS.neonOrange} className="h-full">
            {latestProducts.length > 0 ? (
              <div className="overflow-auto h-full">
                <table className="w-full text-xs">
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>#</th>
                      <th className="text-left py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>สินค้า</th>
                      <th className="text-right py-1 px-1 font-medium" style={{ color: COLORS.textSecondary }}>จำนวน</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latestProducts.map((product, idx) => (
                      <tr key={idx} style={{ borderBottom: `1px solid ${COLORS.gridLine}` }}>
                        <td className="py-1 px-1 font-mono" style={{ color: idx < 3 ? [COLORS.neonYellow, COLORS.neonCyan, COLORS.neonOrange][idx] : COLORS.textSecondary }}>{idx + 1}</td>
                        <td className="py-1 px-1 truncate" style={{ color: COLORS.textPrimary }} title={product.product_description}>{product.product_description || product.product_code}</td>
                        <td className="py-1 px-1 text-right font-mono" style={{ color: COLORS.neonOrange }}>{formatNumber(product.total_quantity)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full"><p style={{ color: COLORS.textSecondary }}>กำลังโหลด...</p></div>
            )}
          </GlowCard>
        </div>


        {/* === Row 3, Col 2-3: เพิ่มเติม === */}
        <div className="col-span-2 h-full min-h-0">
          <GlowCard title="เพิ่มเติมภายหลัง" icon="🔧" glowColor={COLORS.textSecondary} className="h-full">
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: COLORS.textSecondary }}>ช่องว่างสำหรับเพิ่มข้อมูลเพิ่มเติม</p>
            </div>
          </GlowCard>
        </div>

      </main>

      {/* Footer status bar */}
      <div className="fixed bottom-0 left-0 right-0 z-20 px-6 py-1 flex items-center justify-between text-xs" style={{ background: 'rgba(10,14,39,0.9)', borderTop: `1px solid ${COLORS.cardBorder}` }}>
        <div className="flex items-center gap-4">
          <span style={{ color: COLORS.textSecondary }}>
            Last Update: {lastRefresh.toLocaleTimeString('th-TH')}
          </span>
          <span style={{ color: COLORS.neonGreen }}>● LIVE</span>
        </div>
        <div className="flex items-center gap-4">
          <span style={{ color: COLORS.textSecondary }}>Auto-refresh: 60s</span>
          <span style={{ color: COLORS.textSecondary }}>PSI Smart Sale Report v2.0</span>
        </div>
      </div>
    </div>
  );
};

export default MonitorDashboardComponent;
